import { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Dropzone from '../components/Dropzone';
import RecentUploads from '../components/RecentUploads';
import { usePractice } from '../contexts/PracticeContext';
import { useAllowedSources } from '../hooks/useAllowedSources';
import { billingApi } from '../services/billingApi';
import type { BillingSource, UploadSummaryDto } from '../types/billing';

type DropzoneStatus = 'idle' | 'uploading' | 'parsing' | 'done' | 'error';

const SOURCE_LABELS: Record<BillingSource, string> = {
  elation: 'Elation Billing-Home',
  athena: 'athenahealth',
};

export default function Upload() {
  const {
    currentPracticeId,
    isLoading: practiceLoading,
    error: practiceError,
  } = usePractice();
  const navigate = useNavigate();

  const [status, setStatus] = useState<DropzoneStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [recent, setRecent] = useState<UploadSummaryDto[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [skipNotice, setSkipNotice] = useState<string | undefined>();
  const [source, setSource] = useState<BillingSource>('elation');

  // Department-derived source filter (Task 10). Empty/multi = no lock.
  // A single-source response forces `source` and disables the other radio.
  const { allowedSources } = useAllowedSources();
  const restrictedToSource: BillingSource | null =
    allowedSources?.length === 1 ? allowedSources[0] : null;

  useEffect(() => {
    if (restrictedToSource && source !== restrictedToSource) {
      setSource(restrictedToSource);
    }
  }, [restrictedToSource, source]);

  const noPracticeAvailable = !practiceLoading && !currentPracticeId;

  const loadRecent = useCallback(() => {
    if (!currentPracticeId) return;
    setRecentLoading(true);
    billingApi.listUploads(currentPracticeId)
      .then((list) => setRecent(list))
      .catch(() => setRecent([]))
      .finally(() => setRecentLoading(false));
  }, [currentPracticeId]);

  useEffect(() => {
    if (practiceLoading) return;
    if (!currentPracticeId) {
      setRecentLoading(false);
      return;
    }
    loadRecent();
  }, [loadRecent, practiceLoading, currentPracticeId]);

  const handleFile = useCallback(async (file: File) => {
    if (!currentPracticeId) {
      setStatus('error');
      setErrorMessage('No practice assigned to your account. Contact an admin.');
      return;
    }
    setStatus('uploading');
    setErrorMessage(undefined);
    setSkipNotice(undefined);
    try {
      const result = await billingApi.uploadCsv(currentPracticeId, file, source);
      setStatus('done');
      // Pure re-upload: nothing new to triage, so stay on this page with a
      // notice instead of bouncing to an empty per-upload Triage view.
      if (result.rowCount === 0 && result.skippedCount > 0 && result.parseFailedCount === 0) {
        setSkipNotice(`All ${result.skippedCount} claim${result.skippedCount === 1 ? '' : 's'} in this file were already staged for this practice. Nothing new to triage.`);
        loadRecent();
        return;
      }
      const notes: string[] = [];
      if (result.skippedCount > 0) {
        notes.push(`skipped ${result.skippedCount} already-staged`);
      }
      if (result.parseFailedCount > 0) {
        notes.push(`${result.parseFailedCount} row${result.parseFailedCount === 1 ? '' : 's'} dropped during parse (see audit log)`);
      }
      if (notes.length > 0) {
        setSkipNotice(`Staged ${result.rowCount} new claim${result.rowCount === 1 ? '' : 's'}; ${notes.join('; ')}.`);
      }
      navigate(`/uploads/${result.uploadId}/triage`);
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Upload failed.');
    }
  }, [currentPracticeId, navigate, loadRecent, source]);

  const sourceLocked = status === 'uploading' || status === 'parsing' || status === 'done';

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Upload billing CSV</Typography>
      {noPracticeAvailable && (
        <Alert severity={practiceError ? 'error' : 'warning'}>
          {practiceError
            ? `Couldn't load your practices: ${practiceError.message}. Try refreshing.`
            : 'No practice assigned to your account. Contact an admin.'}
        </Alert>
      )}
      <FormControl disabled={sourceLocked}>
        <FormLabel id="upload-source-label">Data Source</FormLabel>
        <RadioGroup
          row
          aria-labelledby="upload-source-label"
          name="upload-source"
          value={source}
          onChange={(e) => setSource(e.target.value as BillingSource)}
        >
          {/* MUI Tooltip needs a <span> wrapper to surface on disabled children. */}
          <Tooltip
            title="Locked by your department assignment."
            disableHoverListener={restrictedToSource !== 'athena'}
          >
            <span>
              <FormControlLabel
                value="elation"
                control={<Radio disabled={restrictedToSource === 'athena'} />}
                label="Elation"
              />
            </span>
          </Tooltip>
          <Tooltip
            title="Locked by your department assignment."
            disableHoverListener={restrictedToSource !== 'elation'}
          >
            <span>
              <FormControlLabel
                value="athena"
                control={<Radio disabled={restrictedToSource === 'elation'} />}
                label="athenahealth"
              />
            </span>
          </Tooltip>
        </RadioGroup>
      </FormControl>
      <Dropzone
        status={status}
        errorMessage={errorMessage}
        onFileSelected={handleFile}
        onClearError={() => { setStatus('idle'); setErrorMessage(undefined); }}
        sourceLabel={SOURCE_LABELS[source]}
      />
      <RecentUploads uploads={recent} isLoading={recentLoading || practiceLoading} />
      <Snackbar
        open={skipNotice !== undefined}
        autoHideDuration={6000}
        onClose={() => setSkipNotice(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={() => setSkipNotice(undefined)}>
          {skipNotice}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
